import type { HTMLAttributes, Ref } from 'react'
import { cx } from '@/shared/lib'
import { getInitials } from './initials'
import type { AvatarTone } from './tone'
import s from './Avatar.module.css'

/** Ступени шкалы: xs — свёрнутый сайдбар, sm — чип и ячейка таблицы, md — строка списка, lg — профиль. */
export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg'

export type AvatarProps = {
  /**
   * Название ЮЛ или ФИО целиком: «ООО «Ситилинк-Опт»», «Иванов Алексей».
   * Инициалы выводятся из него — максимум две буквы, правовая форма отбрасывается.
   * Строка без букв и цифр даёт пустой круг.
   */
  name: string
  size?: AvatarSize
  /** Заливка круга. Опознавательный тон берётся `pickAvatarTone(id)`, `muted` — сущность недоступна. */
  tone?: AvatarTone
  /**
   * Доступное имя. Задано — аватар самостоятельный (`role="img"`), например в свёрнутом сайдбаре.
   * Не задано — аватар декоративный (`aria-hidden`): значит, имя уже видно рядом и читать его
   * второй раз по буквам не нужно.
   */
  label?: string
  ref?: Ref<HTMLSpanElement>
} & Omit<HTMLAttributes<HTMLSpanElement>, 'children'>

// CSS-модуль типизирован индексной сигнатурой, поэтому значения — string | undefined
const SIZE_CLASS: Record<AvatarSize, string | undefined> = {
  xs: s.xs,
  sm: s.sm,
  md: s.md,
  lg: s.lg,
}

const TONE_CLASS: Record<AvatarTone, string | undefined> = {
  accent: s.accent,
  violet: s.violet,
  green: s.green,
  neutral: s.neutral,
  muted: s.muted,
}

/**
 * Круг с инициалами: опознавательная метка контрагента, сотрудника или автора записи.
 * Фото в системе нет — только буквы. Контракт: docs/ui-kit/contracts/avatar.md
 */
export function Avatar({
  name,
  size = 'md',
  tone = 'neutral',
  label,
  className,
  ref,
  ...rest
}: AvatarProps) {
  const initials = getInitials(name)
  const empty = initials === ''

  return (
    <span
      {...rest}
      ref={ref}
      className={cx(s.root, SIZE_CLASS[size], TONE_CLASS[tone], empty && s.empty, className)}
      data-size={size}
      data-tone={tone}
      data-empty={empty ? 'true' : undefined}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      {initials}
    </span>
  )
}
